import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';

interface PercentageSliderProps {
  value: number | null; // stored as decimal (e.g. 12.5 means 12.5%)
  onChange: (decimalValue: number | null) => void;
  label?: string;
  min?: number; // percent domain
  max?: number; // percent domain
  step?: number; // percent domain
  disabled?: boolean;
  required?: boolean;
  hideNumberInput?: boolean;
  id?: string;
}

// The backend expects 0..100 (not 0..1) based on validation logic, so we keep decimal as 0..100.
// Slider shows percent; number input syncs; internal driver always pushes numeric or null.
export const PercentageSlider: React.FC<PercentageSliderProps> = ({
  value,
  onChange,
  label,
  min = 0,
  max = 100,
  step = 0.1,
  disabled = false,
  required = false,
  hideNumberInput = false,
  id
}) => {
  const pct = value ?? 0;

  const handleSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (isNaN(v)) onChange(null); else onChange(v);
  };
  const handleNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') { onChange(null); return; }
    const v = parseFloat(raw);
    if (isNaN(v)) return; // ignore invalid
    onChange(v);
  };

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id}>{label}{required && ' *'}</Label>
      )}
      <div className="flex items-center gap-4">
        <input
          type="range"
          id={id}
          min={min}
          max={max}
          step={step}
          value={pct}
          disabled={disabled}
          onChange={handleSlider}
          className="flex-1 cursor-pointer"
        />
        {!hideNumberInput && (
          <div className="flex items-center gap-1 w-28">
            <Input
              type="number"
              min={min}
              max={max}
              step={step}
              value={value ?? ''}
              disabled={disabled}
              onChange={handleNumber}
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        )}
      </div>
    </div>
  );
};
